import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ConfirmModal from '../components/ConfirmModal';
import useAdminAuth from '../hooks/useAdminAuth';
import { DollarSign, Truck, MapPin, Calendar, User, Phone, CheckCircle, AlertTriangle, CreditCard, Building } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateAU } from '../utils/dateFormat';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminActiveQuotes = () => {
  const navigate = useNavigate();
  useAdminAuth();
  
  const [activeLoads, setActiveLoads] = useState([]);
  const [pendingShipperPayments, setPendingShipperPayments] = useState([]);
  const [stuckAdhocLoads, setStuckAdhocLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Override modals
  const [showAdhocOverrideModal, setShowAdhocOverrideModal] = useState(false);
  const [showShipperOverrideModal, setShowShipperOverrideModal] = useState(false);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [processing, setProcessing] = useState(false);

  const adminEmail = localStorage.getItem('admin_email') || 'info@trucknsendit.com';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendingRes, adhocRes] = await Promise.all([
        axios.get(`${API}/api/admin/pending-shipper-payments?admin_email=${adminEmail}`),
        axios.get(`${API}/api/admin/stuck-adhoc-loads?admin_email=${adminEmail}`)
      ]);
      
      setPendingShipperPayments(pendingRes.data.pending_payments || []);
      setStuckAdhocLoads(adhocRes.data.stuck_loads || []);
      
      // Combine all active loads
      const allActive = [
        ...pendingRes.data.pending_payments.map(l => ({ ...l, payment_type: 'shipper' })),
        ...adhocRes.data.stuck_loads.map(l => ({ ...l, payment_type: 'adhoc' }))
      ];
      setActiveLoads(allActive);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdhocOverride = async () => {
    if (!selectedLoad) return;
    
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/api/admin/fix-adhoc-payment`, null, {
        params: {
          load_id: selectedLoad.id,
          admin_email: adminEmail,
          reason: overrideReason || 'Admin override'
        }
      });
      
      toast.success(response.data.message || 'Adhoc payment marked as complete');
      setShowAdhocOverrideModal(false);
      setSelectedLoad(null);
      setOverrideReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to override adhoc payment');
    } finally {
      setProcessing(false);
    }
  };

  const handleShipperOverride = async () => {
    if (!selectedLoad) return;
    
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/api/admin/shipper-payment/complete`, null, {
        params: {
          load_id: selectedLoad.id,
          admin_email: adminEmail,
          payment_method: 'bank_transfer',
          payment_reference: paymentReference,
          notes: overrideReason || 'Admin manual completion'
        }
      });
      
      toast.success(response.data.message || 'Shipper payment marked as complete');
      setShowShipperOverrideModal(false);
      setSelectedLoad(null);
      setOverrideReason('');
      setPaymentReference('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete shipper payment');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status, paymentType) => {
    if (paymentType === 'adhoc') {
      return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">Awaiting Adhoc Payment</span>;
    }
    
    const badges = {
      'accepted': { color: 'bg-blue-100 text-blue-800', text: 'Awaiting Shipper Payment' },
      'awaiting_shipper_payment': { color: 'bg-blue-100 text-blue-800', text: 'Awaiting Shipper Payment' },
      'awaiting_adhoc_payment': { color: 'bg-orange-100 text-orange-800', text: 'Awaiting Adhoc Payment' },
      'paid': { color: 'bg-green-100 text-green-800', text: 'Paid' }
    };
    
    const badge = badges[status] || { color: 'bg-gray-100 text-gray-800', text: status };
    return <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}>{badge.text}</span>;
  };

  const LoadCard = ({ load, type }) => (
    <Card className="p-4 mb-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-semibold text-[#1e3a5f]">Load #{load.id?.slice(0, 8)}</h3>
          <p className="text-sm text-gray-600">{load.cargo_type}</p>
        </div>
        {getStatusBadge(load.status, type)}
      </div>
      
      <div className="grid md:grid-cols-2 gap-4 text-sm mb-4">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-green-600" />
            <span><strong>From:</strong> {load.pickup_location}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-red-600" />
            <span><strong>To:</strong> {load.dropoff_location}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-600" />
            <span><strong>Available From:</strong> {formatDateAU(load.pickup_date)}</span>
          </div>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-gray-600" />
            <span><strong>Shipper:</strong> {load.shipper_name || load.shipper_email}</span>
          </div>
          {load.shipper_phone && (
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-gray-600" />
              <span>{load.shipper_phone}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-gray-600" />
            <span><strong>Carrier:</strong> {load.carrier_name || load.assigned_carrier}</span>
          </div>
          {load.quote_amount && (
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-600" />
              <span><strong>Amount:</strong> ${load.quote_amount?.toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="flex gap-2 pt-3 border-t">
        {type === 'adhoc' && (
          <Button
            onClick={() => {
              setSelectedLoad(load);
              setShowAdhocOverrideModal(true);
            }}
            className="bg-orange-600 hover:bg-orange-700"
            size="sm"
            data-testid={`override-adhoc-${load.id}`}
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            Complete Adhoc Payment
          </Button>
        )}
        
        {type === 'shipper' && (
          <Button
            onClick={() => {
              setSelectedLoad(load);
              setShowShipperOverrideModal(true);
            }}
            className="bg-blue-600 hover:bg-blue-700"
            size="sm"
            data-testid={`override-shipper-${load.id}`}
          >
            <CreditCard className="w-4 h-4 mr-1" />
            Mark as Paid (Bank Transfer)
          </Button>
        )}
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/load/${load.id}`)}
        >
          View Details
        </Button>
      </div>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-[#1e3a5f] flex items-center gap-2">
              <DollarSign className="w-8 h-8" />
              Active Quotes & Payments
            </h1>
            <p className="text-gray-600 mt-1">
              Manage quotes awaiting payment and override if needed
            </p>
          </div>
          
          <Button onClick={fetchData} variant="outline">
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <Card className="p-4 bg-orange-50 border-orange-200">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-orange-600" />
              <div>
                <p className="text-2xl font-bold text-orange-800">{stuckAdhocLoads.length}</p>
                <p className="text-sm text-orange-600">Awaiting Adhoc Payment</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="flex items-center gap-3">
              <CreditCard className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-800">{pendingShipperPayments.length}</p>
                <p className="text-sm text-blue-600">Awaiting Shipper Payment</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-gray-50 border-gray-200">
            <div className="flex items-center gap-3">
              <Truck className="w-8 h-8 text-gray-600" />
              <div>
                <p className="text-2xl font-bold text-gray-800">{activeLoads.length}</p>
                <p className="text-sm text-gray-600">Total Active</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="all">All Active ({activeLoads.length})</TabsTrigger>
            <TabsTrigger value="adhoc">Adhoc Payments ({stuckAdhocLoads.length})</TabsTrigger>
            <TabsTrigger value="shipper">Shipper Payments ({pendingShipperPayments.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p>Loading...</p>
              </div>
            ) : activeLoads.length === 0 ? (
              <Card className="p-12 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">All Clear!</h3>
                <p className="text-gray-500">No quotes are currently awaiting payment.</p>
              </Card>
            ) : (
              <div>
                {stuckAdhocLoads.map(load => (
                  <LoadCard key={load.id} load={load} type="adhoc" />
                ))}
                {pendingShipperPayments.map(load => (
                  <LoadCard key={load.id} load={load} type="shipper" />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="adhoc">
            {stuckAdhocLoads.length === 0 ? (
              <Card className="p-12 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600">No Stuck Adhoc Payments</h3>
              </Card>
            ) : (
              stuckAdhocLoads.map(load => (
                <LoadCard key={load.id} load={load} type="adhoc" />
              ))
            )}
          </TabsContent>

          <TabsContent value="shipper">
            {pendingShipperPayments.length === 0 ? (
              <Card className="p-12 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600">No Pending Shipper Payments</h3>
              </Card>
            ) : (
              pendingShipperPayments.map(load => (
                <LoadCard key={load.id} load={load} type="shipper" />
              ))
            )}
          </TabsContent>
        </Tabs>

        {/* Adhoc Override Modal */}
        {showAdhocOverrideModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md p-6">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-orange-600" />
                Complete Adhoc Payment
              </h2>
              
              <p className="text-gray-600 mb-4">
                This will mark the adhoc carrier's $49 job fee as paid and allow the shipper to proceed with payment.
              </p>
              
              {selectedLoad && (
                <div className="bg-gray-50 p-3 rounded mb-4 text-sm">
                  <p><strong>Load:</strong> #{selectedLoad.id?.slice(0, 8)}</p>
                  <p><strong>Carrier:</strong> {selectedLoad.carrier_name || selectedLoad.assigned_carrier}</p>
                  <p><strong>Route:</strong> {selectedLoad.pickup_location} → {selectedLoad.dropoff_location}</p>
                </div>
              )}
              
              <div className="mb-4">
                <Label>Reason for Override</Label>
                <Input
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="e.g., Promo code used, Admin waiver"
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={handleAdhocOverride}
                  disabled={processing}
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                >
                  {processing ? 'Processing...' : 'Complete Payment'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAdhocOverrideModal(false);
                    setSelectedLoad(null);
                    setOverrideReason('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Shipper Override Modal */}
        {showShipperOverrideModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md p-6">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-600" />
                Mark Shipper Payment Complete
              </h2>
              
              <p className="text-gray-600 mb-4">
                Use this when the shipper has paid via bank transfer or other offline method.
              </p>
              
              {selectedLoad && (
                <div className="bg-gray-50 p-3 rounded mb-4 text-sm">
                  <p><strong>Load:</strong> #{selectedLoad.id?.slice(0, 8)}</p>
                  <p><strong>Shipper:</strong> {selectedLoad.shipper_name || selectedLoad.shipper_email}</p>
                  <p><strong>Amount:</strong> ${selectedLoad.quote_amount?.toLocaleString() || 'N/A'}</p>
                </div>
              )}
              
              <div className="space-y-4 mb-4">
                <div>
                  <Label>Payment Reference (e.g., Bank Transfer ID)</Label>
                  <Input
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                    placeholder="e.g., TRF-123456"
                  />
                </div>
                
                <div>
                  <Label>Notes</Label>
                  <Input
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder="e.g., Verified bank transfer received"
                  />
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={handleShipperOverride}
                  disabled={processing}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {processing ? 'Processing...' : 'Mark as Paid'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowShipperOverrideModal(false);
                    setSelectedLoad(null);
                    setOverrideReason('');
                    setPaymentReference('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default AdminActiveQuotes;
